// De prijstabel uit temp/calc_v2.tsv: "YARD VELVETLINE POLYCARBONAAT" en
// "YARD VELVETLINE GLAS". Elke rij is een compleet dak ("cap") inclusief BTW en
// inclusief het glas of polycarbonaat, dus de losse artikelen in src/prices.ts
// tellen onder dit model niet meer mee in de totaalprijs.
//
// Opzoekregel, letterlijk uit het blad:
//   "Vind eerstvolgende breedte op basis van >= ingevoerde breedte"
//   "Vind eerstvolgende diepte  op basis van >= ingevoerde diepte"

export type VelvetlineMaterial = "poly" | "glas";

export interface VelvetlinePriceRow {
  /** Artikelnummer uit het blad, puur ter herleiding. */
  article: string;
  /** Breedte in mm (blad: 305,8 cm -> 3058 mm). */
  width: number;
  /** Diepte in mm (blad: 250 cm -> 2500 mm). */
  depth: number;
  /** Kolom 4 - het standaard aantal staanders voor deze maat. */
  staanders: number;
  /** Kolom 5 - "CAP Incl. BTW", de prijs bij het standaard aantal staanders. */
  price: number;
  /**
   * Kolom 6 - "Cap Prijs 2 staanders". Dit is een vervangende prijs, geen
   * meerprijs: bij 2 staanders kost het dak dit bedrag in plaats van `price`.
   * `null` waar 2 staanders al de standaard is en de kolom dus leeg staat.
   */
  price2Staanders: number | null;
  /**
   * Kolom 7 t/m 9 - "Extra materiaal bij 2 staanders": de goot moet dan over
   * een grotere overspanning, dus komt er versteviging bij. Elk artikelnummer
   * hoort bij precies een kolom, zie VELVETLINE_EXTRA_LABELS. Leeg waar het
   * blad die kolommen leeg laat - dat is overal waar 2 staanders niet te
   * kiezen valt, dus waar kolom 6 ook leeg staat.
   */
  extras: string[];
}

// De kolomkoppen 7 t/m 9 van het blad, per artikelnummer.
export const VELVETLINE_EXTRA_LABELS: Record<string, string> = {
  Y530055: "Gootversteviging strip",
  Y113101: "Gootversteviging 11x11",
  Y605067: "Gootversteviging 11x15",
};

// prettier-ignore
export const VELVETLINE_POLY: VelvetlinePriceRow[] = [
  { article: "Y601001", width: 3058, depth: 2500, staanders: 2, price: 1310, price2Staanders: null, extras: [] },
  { article: "Y601002", width: 4058, depth: 2500, staanders: 2, price: 1655, price2Staanders: null, extras: [] },
  { article: "Y601003", width: 5058, depth: 2500, staanders: 3, price: 1985, price2Staanders: 2390, extras: ["Y530055"] },
  { article: "Y601004", width: 6058, depth: 2500, staanders: 3, price: 2300, price2Staanders: 3095, extras: ["Y530055", "Y113101"] },
  { article: "Y601005", width: 7058, depth: 2500, staanders: 3, price: 2645, price2Staanders: 4025, extras: ["Y530055", "Y605067"] },
  { article: "Y601006", width: 3058, depth: 3000, staanders: 2, price: 1430, price2Staanders: null, extras: [] },
  { article: "Y601007", width: 4058, depth: 3000, staanders: 2, price: 1760, price2Staanders: null, extras: [] },
  { article: "Y601008", width: 5058, depth: 3000, staanders: 3, price: 2085, price2Staanders: 2470, extras: ["Y530055"] },
  { article: "Y601009", width: 6058, depth: 3000, staanders: 3, price: 2405, price2Staanders: 3165, extras: ["Y530055", "Y113101"] },
  { article: "Y601010", width: 7058, depth: 3000, staanders: 3, price: 2725, price2Staanders: 4045, extras: ["Y530055", "Y605067"] },
  { article: "Y601011", width: 3058, depth: 3500, staanders: 2, price: 1635, price2Staanders: null, extras: [] },
  { article: "Y601012", width: 4058, depth: 3500, staanders: 2, price: 1955, price2Staanders: null, extras: [] },
  { article: "Y601013", width: 5058, depth: 3500, staanders: 3, price: 2275, price2Staanders: 2660, extras: ["Y530055"] },
  { article: "Y601014", width: 6058, depth: 3500, staanders: 3, price: 2595, price2Staanders: 3350, extras: ["Y530055", "Y113101"] },
  { article: "Y601015", width: 7058, depth: 3500, staanders: 3, price: 2910, price2Staanders: 4230, extras: ["Y530055", "Y605067"] },
  { article: "Y601016", width: 3058, depth: 4000, staanders: 2, price: 1825, price2Staanders: null, extras: [] },
  { article: "Y601017", width: 4058, depth: 4000, staanders: 2, price: 2140, price2Staanders: null, extras: [] },
  { article: "Y601018", width: 5058, depth: 4000, staanders: 3, price: 2460, price2Staanders: 2845, extras: ["Y530055"] },
  { article: "Y601019", width: 6058, depth: 4000, staanders: 3, price: 2780, price2Staanders: 3880, extras: ["Y530055", "Y605067"] },
  { article: "Y601020", width: 7058, depth: 4000, staanders: 3, price: 3080, price2Staanders: 4400, extras: ["Y530055", "Y605067"] },
];

// prettier-ignore
export const VELVETLINE_GLAS: VelvetlinePriceRow[] = [
  { article: "Y602001", width: 3058, depth: 2500, staanders: 2, price: 1995, price2Staanders: null, extras: [] },
  { article: "Y602002", width: 4058, depth: 2500, staanders: 3, price: 2420, price2Staanders: 2820, extras: ["Y530055"] },
  { article: "Y602003", width: 5058, depth: 2500, staanders: 3, price: 2845, price2Staanders: 3245, extras: ["Y530055"] },
  { article: "Y602004", width: 6058, depth: 2500, staanders: 3, price: 3265, price2Staanders: 4465, extras: ["Y530055", "Y113101"] },
  { article: "Y602005", width: 7058, depth: 2500, staanders: 4, price: 3690, price2Staanders: 5575, extras: ["Y530055", "Y605067"] },
  { article: "Y602006", width: 3058, depth: 3000, staanders: 2, price: 2320, price2Staanders: null, extras: [] },
  { article: "Y602007", width: 4058, depth: 3000, staanders: 3, price: 2745, price2Staanders: 3150, extras: ["Y530055"] },
  { article: "Y602008", width: 5058, depth: 3000, staanders: 3, price: 3170, price2Staanders: 3575, extras: ["Y530055"] },
  { article: "Y602009", width: 6058, depth: 3000, staanders: 3, price: 3595, price2Staanders: 5220, extras: ["Y530055", "Y605067"] },
  { article: "Y602010", width: 7058, depth: 3000, staanders: 4, price: 4025, price2Staanders: 5940, extras: ["Y530055", "Y605067"] },
  { article: "Y602011", width: 3058, depth: 3500, staanders: 2, price: 2710, price2Staanders: null, extras: [] },
  { article: "Y602012", width: 4058, depth: 3500, staanders: 3, price: 3185, price2Staanders: 3585, extras: ["Y530055"] },
  { article: "Y602013", width: 5058, depth: 3500, staanders: 3, price: 3775, price2Staanders: 4175, extras: ["Y530055"] },
  { article: "Y602014", width: 6058, depth: 3500, staanders: 3, price: 4365, price2Staanders: 6065, extras: ["Y530055", "Y605067"] },
  { article: "Y602015", width: 7058, depth: 3500, staanders: 4, price: 4895, price2Staanders: 6900, extras: ["Y530055", "Y605067"] },
  { article: "Y602016", width: 3058, depth: 4000, staanders: 2, price: 4105, price2Staanders: null, extras: [] },
  { article: "Y602017", width: 4058, depth: 4000, staanders: 3, price: 5015, price2Staanders: 5420, extras: ["Y530055"] },
  { article: "Y602018", width: 5058, depth: 4000, staanders: 3, price: 5800, price2Staanders: 6200, extras: ["Y530055"] },
  { article: "Y602019", width: 6058, depth: 4000, staanders: 3, price: 6715, price2Staanders: 7860, extras: ["Y530055", "Y605067"] },
  { article: "Y602020", width: 7058, depth: 4000, staanders: 4, price: 7430, price2Staanders: 8810, extras: ["Y530055", "Y605067"] },
];

const TABLES: Record<VelvetlineMaterial, VelvetlinePriceRow[]> = {
  poly: VELVETLINE_POLY,
  glas: VELVETLINE_GLAS,
};

// De grootste maat die het blad kent. Bredere of diepere overkappingen worden
// hierop afgekapt, zie findVelvetlineRow.
export const VELVETLINE_MAX_WIDTH = 7058;
export const VELVETLINE_MAX_DEPTH = 4000;

// Buiten het bereik van het blad valt er geen aantal staanders af te lezen, dus
// daar geldt een gewone overspanningsgrens per materiaal. Glas heeft een
// kortere overspanning nodig dan polycarbonaat omdat het zwaarder is. Binnen het
// blad worden deze waarden niet gebruikt: daar bepaalt kolom 4 het aantal
// staanders, zie resolveVelvetlineSpan.
export const VELVETLINE_MAX_SPAN: Record<VelvetlineMaterial, number> = {
  poly: 4000,
  glas: 3000,
};

// De 3D (house.column_size) overspant `breedte - 2 * COLUMN_SIZE` tussen de
// buitenste staanders en zet er een tussenstaander bij zodra een vak de
// frontlimit overschrijdt. De 110 is de staanderbreedte van Velvetline; hij is
// nagelopen in het bundeltje dat ook echt draait:
//   grep -o "column_size:110" public/bundle.js
// Zit de overkapping daar straks anders in elkaar, dan klopt niet alleen de
// tekening niet meer maar ook de staander-meerprijs.
export const COLUMN_SIZE = 110;

// De breedste overkapping die de breedte-invoer toelaat. Ook de frontlimit die
// "de klant wil expliciet 2 staanders" betekent: zo breed dat de 3D nooit een
// tussenstaander plaatst.
export const WIDTH_MAX = 12000;

// frontlimit is in de praktijk een boolean: WIDTH_MAX betekent "de klant koos
// expliciet 2 staanders", elke andere waarde betekent "laat de tabel het
// zeggen". Het getal zelf heeft geen betekenis meer - de overspanning die de 3D
// aanhoudt wordt per maat afgeleid, niet opgeslagen. Het blijft 4000 omdat
// bestaande offertes die waarde al in dimensions hebben staan en zo zonder
// migratie als "automatisch" teruglezen.
export const FRONTLIMIT_AUTO = 4000;

export const isTwoPostsChosen = (frontlimit: number): boolean =>
  frontlimit === WIDTH_MAX;

export const resolveVelvetlineMaterial = (glass?: string): VelvetlineMaterial =>
  glass === "glass" ? "glas" : "poly";

// De breedte die het dak werkelijk moet overbruggen tussen de twee buitenste
// staanders, wat de 3D tegen de frontlimit afzet.
export const getSpannedWidth = (width: number): number =>
  width - 2 * COLUMN_SIZE;

// Eerstvolgende rij met breedte >= en diepte >= de invoer. Buiten het bereik
// van het blad valt de opzoeking terug op de grootste maat, zodat er altijd
// een prijs is. Let op: een overkapping van 12 x 5 m krijgt daarmee dezelfde
// prijs als 7,06 x 4 m.
export const findVelvetlineRow = (
  material: VelvetlineMaterial,
  width: number,
  depth: number
): VelvetlinePriceRow | undefined => {
  const targetWidth = Math.min(width, VELVETLINE_MAX_WIDTH);
  const targetDepth = Math.min(depth, VELVETLINE_MAX_DEPTH);

  return TABLES[material].find(
    (row) => row.width >= targetWidth && row.depth >= targetDepth
  );
};

// Letterlijk de vakverdeling die de 3D maakt, uit Configurator.generateWalls()
// van vankooten.3js (in public/bundle.js terug te vinden op "size_limits.width").
// Het aantal staanders op de voorgevel is dit aantal vakken + 1. Merk op dat de
// engine de tussenstaanders van de over te spannen breedte aftrekt; een simpele
// ceil(breedte / frontlimit) geeft niet overal hetzelfde antwoord.
export const getEngineBayCount = (
  spannedWidth: number,
  frontlimit: number
): number => {
  let count = 1;
  let bay = spannedWidth;

  while (bay > frontlimit) {
    count++;
    bay = (spannedWidth - (count - 1) * COLUMN_SIZE) / count;
  }

  return count;
};

// De frontlimit waarbij de engine precies op `bays` vakken uitkomt: de lengte
// van zo'n vak. Vakken worden korter naarmate het er meer zijn, dus deze limiet
// laat de lus bij `bays` stoppen en niet eerder. Naar boven afgerond, zodat een
// afrondingsverschil in de deling de lus geen vak verder duwt.
const getFrontlimitForBays = (spannedWidth: number, bays: number): number =>
  Math.ceil((spannedWidth - (bays - 1) * COLUMN_SIZE) / bays);

export interface VelvetlineSpan {
  /** De overspanning die aan de 3D wordt doorgegeven. */
  frontlimit: number;
  /** Het aantal staanders op de voorgevel dat daaruit volgt. */
  staanders: number;
}

/**
 * Overspanning en aantal staanders voor een maat.
 *
 * Binnen het blad is kolom 4 de baas: de klant betaalt de rij die bij zijn maat
 * hoort (eerstvolgende breedte >=), dus die rij bepaalt ook hoeveel staanders
 * de voorgevel krijgt. Daarbuiten bestaat die rij niet en valt het terug op een
 * gewone overspanningsgrens, anders zou een overkapping van 12m op de drie
 * staanders van de 705,8-rij komen te staan.
 *
 * Het aantal staanders wordt niet naast de 3D uitgerekend maar eruit afgeleid:
 * dezelfde vakverdeling, dezelfde frontlimit. Tekening en offerte kunnen zo niet
 * uit elkaar lopen.
 */
export const resolveVelvetlineSpan = (
  width: number,
  depth: number,
  material: VelvetlineMaterial,
  twoPostsChosen: boolean
): VelvetlineSpan => {
  const spannedWidth = getSpannedWidth(width);
  const row = findVelvetlineRow(material, width, depth);

  let frontlimit: number;
  if (twoPostsChosen) {
    frontlimit = WIDTH_MAX;
  } else if (width > VELVETLINE_MAX_WIDTH || !row) {
    frontlimit = VELVETLINE_MAX_SPAN[material];
  } else {
    frontlimit = getFrontlimitForBays(spannedWidth, row.staanders - 1);
  }

  return {
    frontlimit,
    staanders: getEngineBayCount(spannedWidth, frontlimit) + 1,
  };
};

// Waar het blad zelf al 2 staanders voorschrijft is er niets te kiezen: kolom 6
// staat leeg, dus JA en NEE zouden hetzelfde kosten en hetzelfde tekenen.
export const isForcedTwoPosts = (
  width: number,
  depth: number,
  material: VelvetlineMaterial
): boolean => {
  const row = findVelvetlineRow(material, width, depth);

  return !row || row.price2Staanders === null;
};

export interface VelvetlineExtraMaterial {
  article: string;
  label: string;
  /** Kolom 2 en 3 van de rij waar dit artikel bij hoort, in mm. */
  width: number;
  depth: number;
}

// Het extra materiaal uit kolom 7 t/m 9, met de maat van de rij waar het bij
// hoort: de goot van een 605,8 x 250 wordt anders verstevigd dan die van een
// 705,8 x 400, dus zonder die maat is het artikelnummer niet te bestellen.
export const getVelvetlineExtras = (
  material: VelvetlineMaterial,
  width: number,
  depth: number,
  twoPosts: boolean
): VelvetlineExtraMaterial[] => {
  if (!twoPosts) return [];

  const row = findVelvetlineRow(material, width, depth);
  if (!row) return [];

  return row.extras.map((article) => ({
    article,
    label: VELVETLINE_EXTRA_LABELS[article] ?? article,
    width: row.width,
    depth: row.depth,
  }));
};

export const getVelvetlinePrice = (
  material: VelvetlineMaterial,
  width: number,
  depth: number,
  twoPosts: boolean
): number => {
  const row = findVelvetlineRow(material, width, depth);
  if (!row) return 0;

  // Kolom 6 bestaat alleen waar het blad meer dan 2 staanders voorschrijft;
  // waar 2 staanders al standaard is, staat de prijs gewoon in kolom 5.
  if (twoPosts && row.price2Staanders !== null) return row.price2Staanders;

  return row.price;
};
